import { Info } from 'lucide-react';
import { ElementRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductCard } from './ProductCard';

import { AnimateSpin } from '@/components/AnimateSpin';
import { SearchProductsFilter } from '@/components/SearchProductsFilter';
import { SearchProductsForm } from '@/components/SearchProductsForm';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { storageKeys } from '@/config/storageKeys';
import { withSearchProductsFilters } from '@/hocs/withSearchProductsFilter';
import { useGetProducts } from '@/hooks/useGetProducts';

interface ProductSelectProps {
  isSelected: boolean;
}

export const ProductSelect = withSearchProductsFilters<ProductSelectProps>(
  ({ isSelected, searchFilters, handleSearchFilters, handleResetFilters }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [addToFirstPosition, setAddToFirstPosition] = useState(
      () =>
        localStorage.getItem(storageKeys.addSliceProductToFirstPosition) !==
        'false',
    );

    const endProductsRef = useRef<ElementRef<'li'>>(null);

    const { t } = useTranslation('translation', {
      keyPrefix:
        'pages.MerchandisingCollections.pages.Slice.components.ProductSelect',
    });

    const {
      products,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
    } = useGetProducts({ ...searchFilters, unique: true });

    useEffect(() => {
      if (!endProductsRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const { isIntersecting } = entries[0];

          if (!hasNextPage) {
            obs.disconnect();
            return;
          }

          if (isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '30px',
        },
      );

      observer.observe(endProductsRef.current);

      return () => {
        observer.disconnect();
      };
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    useEffect(() => {
      if (!isSelected) {
        handleResetFilters();
      }
    }, [isSelected, handleResetFilters]);

    function handleAddToFirstPositionChange(checked: boolean) {
      setAddToFirstPosition(checked);
      localStorage.setItem(
        storageKeys.addSliceProductToFirstPosition,
        String(checked),
      );
    }

    return (
      <div className="space-y-8">
        <div className="relative flex-1">
          <SearchProductsForm
            onSearchFilters={handleSearchFilters}
            searchFilters={searchFilters}
          />

          <SearchProductsFilter
            onSearchFilters={handleSearchFilters}
            searchFilters={searchFilters}
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="add-to-first-position"
            checked={addToFirstPosition}
            onCheckedChange={(checked) =>
              handleAddToFirstPositionChange(checked === true)
            }
          />

          <Label htmlFor="add-to-first-position" className="cursor-pointer">
            {t('addToFirstPosition')}
          </Label>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 shrink-0 cursor-pointer text-muted-foreground" />
              </TooltipTrigger>

              <TooltipContent className="max-w-[260px]">
                <span>{t('addToFirstPositionTooltip')}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {products.map((product) => (
            <li key={product.id}>
              <ProductCard
                product={product}
                isSaving={isSaving}
                onSaving={setIsSaving}
                addToFirstPosition={addToFirstPosition}
              />
            </li>
          ))}

          <li ref={endProductsRef}>{isFetching && <AnimateSpin />}</li>
        </ul>
      </div>
    );
  },
);
